import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { 
  Send, Sparkles, User, Bot, RefreshCw, Lightbulb
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";

const CopilotPage = () => {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I'm your AI Sales Copilot. I can help you with:\n\n• Finding leads based on criteria\n• Getting insights about pipeline performance\n• Recommendations for follow-ups\n• Answering questions about your sales data\n\nWhat would you like to know?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/copilot`, {
        query: userMessage
      });
      
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: response.data.response 
      }]);
    } catch (error) {
      console.error("Copilot error:", error);
      toast.error("Failed to get response");
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm sorry, I encountered an error. Please try again." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedQueries = [
    "Which leads are most likely to book this week?",
    "Show me leads that need follow-up today",
    "What are our top performing lead sources?",
    "Which leads haven't been contacted in a week?",
    "Summarize today's tasks"
  ];

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 h-[calc(100vh-4rem)] flex flex-col" data-testid="copilot-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">AI Sales Copilot</h1>
            <p className="text-slate-500">Powered by GPT-5.2</p>
          </div>
        </div>

        {/* Chat Area */}
        <Card className="flex-1 bg-white border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <CardContent className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' ? 'bg-slate-900' : 'bg-amber-100'
                  }`}>
                    {msg.role === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-amber-600" />
                    )}
                  </div>
                  <div className={`max-w-[80%] p-4 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'bg-slate-900 text-white' 
                      : 'bg-slate-100 text-slate-900'
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="bg-slate-100 p-4 rounded-2xl">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin text-slate-500" />
                      <span className="text-slate-500">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </CardContent>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-4">
              <p className="text-sm text-slate-500 mb-2 flex items-center gap-1">
                <Lightbulb className="w-4 h-4" />
                Try asking:
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestedQueries.map((query, idx) => (
                  <button
                    key={idx}
                    className="text-sm px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-700 transition-colors"
                    onClick={() => setInput(query)}
                    data-testid={`suggestion-${idx}`}
                  >
                    {query}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex gap-2">
              <Input
                data-testid="copilot-input"
                placeholder="Ask me anything about your leads..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                className="flex-1"
              />
              <Button 
                data-testid="copilot-send-btn"
                onClick={handleSend} 
                disabled={!input.trim() || loading}
                className="bg-slate-900 hover:bg-slate-800"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default CopilotPage;
